import React, { useState, useContext, useEffect, useCallback, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { TextField, Button, Tooltip, FormHelperText } from '@material-ui/core';
import MicIcon from '@material-ui/icons/Mic';
import StopRoundedIcon from '@material-ui/icons/StopRounded';
import PageLoader from '../../../components/PageLoader';
import {
  getSpecNotes,
  postSpecNotes,
  SuccessMsg as successMessage,
  resetSpecialNote,
  setNotesReduxState,
} from '../../../redux/reducers/SpecNotes';
import { useUpdateEffect } from 'react-use';
import Accordion from '../../../util/CoreWindowsAccordion';
import { useLocation } from 'react-router-dom';
import { ErrorSnackbar, SuccessSnackbar } from '../../../components/CustomSnackBar';
import Filter from 'bad-words';
import debounce from 'lodash.debounce';
import { profinityWords } from '../../../util/ProfinityWord';
import { LANGUAGE_CODE, REGION } from '../../../constants/ReusableConstants';
import { ThemeContext } from '../../../themes/ThemeProvider';
import { SPLIT_SCREEN } from '../../../constants/WorkQueueConstants';

import useUnsavedChangesWarning from '../../../util/CustomHooks';
import { getSesssionData } from '../../../util/Functions';
import { updateModifiedWindows, updateWindowErrors } from '../../../redux/reducers/ModifiedWindows';
import * as Yup from 'yup';
import { values, isEmpty } from 'lodash';
import { IMPORTANT_NOTES } from '../../../util/Content';
import { getWindowdetails } from '../../../redux/reducers/SaveAllWindows';
import { handleResetButton } from '../../../redux/reducers/ModifiedWindows';
import FormUpdatedContext from '../../../contexts/FormUpdatedContext';
import SaveWindowDataContext from '../../../contexts/SaveWindowDataContext';
import ResetWindowDataContext from '../../../contexts/ResetWindowDataContext';
import { useSaveWindow } from '../../../hooks/useSaveWindow';

export function getSpecialNotesPayload({ data, accNum, id }) {
  return {
    notes: data && data.notes ? data.notes : '',
    accountNumber: accNum,
  };
}

export const getSpecialNotesInitialVal = (data, single) => {
  const obj = {
    notes: data && data.notes ? data.notes : '',
    accountNumber: data?.accountNumber || '',
  };
  if (single) {
    return obj[single];
  } else {
    return obj;
  }
};

const audioUtils = require('./audioUtils'); // for encoding audio data as PCM
const crypto = require('crypto'); // tot sign our pre-signed URL
const v4 = require('./aws-signature-v4'); // to generate our pre-signed URL
const marshaller = require('@aws-sdk/eventstream-marshaller'); // for converting binary event stream messages to and from JSON
const utilUtf8Node = require('@aws-sdk/util-utf8-node'); // utilities for encoding and decoding UTF8
const MicrophoneStream = require('microphone-stream'); // collect microphone input as a stream of raw bytes

// pre-signed url
const sampleRate = 44100;
const languageCode = LANGUAGE_CODE;
const region = REGION;
const { REACT_APP_ACCESS_KEY, REACT_APP_SECRET_KEY } = process.env;
let transcription = '';
let text = '';

// our converter between binary event streams messages and JSON
const eventStreamMarshaller = new marshaller.EventStreamMarshaller(
  utilUtf8Node.toUtf8,
  utilUtf8Node.fromUtf8,
);
const markAsModified = ({ field, newVal, oldVal, label }, dirty) => {
  dirty && dirty();
  const obj = getSesssionData('modifiedWindowsFields');
  const valueObj = {
    oldVal: oldVal,
    newVal: newVal,
    label,
  };
  if (obj[IMPORTANT_NOTES]) {
    obj[IMPORTANT_NOTES][field] = valueObj;
  } else {
    obj[IMPORTANT_NOTES] = {};
    obj[IMPORTANT_NOTES][field] = valueObj;
  }
  sessionStorage.setItem('modifiedWindowsFields', JSON.stringify(obj));
};

function getAudioEventMessage(buffer) {
  // wrap the audio data in a JSON envelope
  return {
    headers: {
      ':message-type': {
        type: 'string',
        value: 'event',
      },
      ':event-type': {
        type: 'string',
        value: 'AudioEvent',
      },
    },
    body: buffer,
  };
}

const SpecialNotes = ({ label }) => {
  const { labelColor } = useContext(ThemeContext);
  const useStyles = makeStyles((theme) => ({
    labelName: {
      '& .MuiOutlinedInput-input': {
        color: `${labelColor} !important`,
        fontSize: '13px',
        padding: '10px 0 !important',
      },
    },
    inputLabel: {
      '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
        fontWeight: '600',
      },
    },
    accordionDetailsStyling: {
      backgroundColor: '#ffffff',
      borderRadius: '0px 0px 8px 8px',
      marginTop: 10,
    },
    detailsText: {
      fontSize: '0.8rem',
      fontWeight: '500',
      backgroundColor: '#FFF',
      padding: 10,
      boxShadow:
        '0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)',
    },
    specialNeeds: {
      position: 'relative',
      display: 'flex',
      justifyContent: 'flex-end',
      padding: '12px 5px 5px',
      '& .MuiFormControl-marginDense': {
        marginTop: '8px !important',
        marginBottom: '4px !important',
      },
      '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
        fontSize: '16px !important',
        padding: '3px 10px !important',
      },
      '& fieldset': {
        top: '-5px !important',
        '& legend': {
          display: 'block !important',
        },
      },
    },
    micButton: {
      position: 'absolute',
      top: -20,
      right: '40px',
      alignItems: 'flex-start',

      minWidth: '30px !important',
      zIndex: 1,
      color: 'red',
    },
    stopButton: {
      position: 'absolute',
      top: -20,
      alignItems: 'flex-start',
      minWidth: '30px !important',
      zIndex: 1,
      color: 'blue',
    },
    buttonRoot: {
      lineHeight: 1.75,
      [theme.breakpoints.down('md')]: {
        lineHeight: 1.5,
        padding: '6px 14px',
      },
      '& .MuiButton-label': {
        fontSize: '0.813rem',
        [theme.breakpoints.down('md')]: {
          fontSize: '0.688rem',
          letterSpacing: 1,
        },
        '& .MuiFormHelperText-root': {
          position: 'absolute',
          bottom: 0,
          top: 35,
        },
      },
    },
    notesError: {
      '& .MuiFormHelperText-root': {
        bottom: '-10px !important',
        left: '10px !important',
        position: 'absolute',
      },
    },
    heading: {
      fontSize: '0.75rem',
    },
  }));
  const [play, setPlay] = useState(false);
  const [validateNotes, setValidateNotes] = useState(false);
  const [cancel, setCancel] = useState(false);
  const dispatch = useDispatch();
  const location = useLocation();
  const accountNumber = location?.state?.accountNumber;
  const { onDirty } = useUnsavedChangesWarning();
  const { specNotesData, specNotesStatus, isLoading, successMsg, errorMsg } = useSelector(
    ({ specNotes }) => specNotes,
  );
  const { modifiedWindowsList } = useSelector(({ modifiedWindows }) => modifiedWindows);
  useEffect(() => {
    accountNumber && dispatch(getSpecNotes(accountNumber));
  }, [dispatch, specNotesStatus, accountNumber]);
  const [charLimitMsg, setCharLimitMsg] = useState(false);
  const [notes, setNotes] = useState('');
  const [save, setSave] = useState(false);
  useEffect(() => {
    stop(webSocket);
    transcription = '';
    if (specNotesStatus === 200) {
      setSave(true);
      setValidateNotes(false);
      setCharLimitMsg(false);
      dispatch(resetSpecialNote());
    }
  }, [specNotesStatus]);
  const initialValues =
    modifiedWindowsList && modifiedWindowsList[IMPORTANT_NOTES]?.modified
      ? modifiedWindowsList[IMPORTANT_NOTES]?.data
      : getSpecialNotesInitialVal(specNotesData);

  useEffect(() => {
    return () => {
      const sessionData = getSesssionData('modifiedWindowsFields');

      if (sessionData && sessionData[IMPORTANT_NOTES]) {
        const formValues = getValues();
        dispatch(
          updateModifiedWindows({
            windowName: IMPORTANT_NOTES,
            modified: true,
            data: formValues,
            payload: {
              data: getSpecialNotesPayload({
                data: formValues,
              }),
              misc: { isDirty },
            },
          }),
        );
      }
    };
  }, []);

  useEffect(() => {
    dispatch(
      getWindowdetails({
        windowNames: ['Important Notes'],
      }),
    );
  }, []);
  const {
    handleSubmit,
    errors,
    reset,
    getValues,
    trigger,
    register,
    formState: { isDirty },
  } = useForm({
    defaultValues: initialValues,
    mode: 'all',
    resolver: async (data) => {
      try {
        await validationSchema.validate(data, { abortEarly: false });
        dispatch(
          updateWindowErrors({ windowName: 'Important Notes', errors: null, hasError: false }),
        );

        return { values: data, errors: {} };
      } catch (err) {
        const formattedErrors = {};
        // Format Yup validation errors into an object
        err.inner.forEach((error) => {
          formattedErrors[error.path] = { message: error.message };
        });

        // Dispatch the action to store errors in Redux
        const haveErrors = values(formattedErrors).every(isEmpty);
        if (!haveErrors) {
          dispatch(
            updateWindowErrors({
              windowName: 'Important Notes',
              errors: formattedErrors,
              hasError: true,
              trigger,
            }),
          );
        } else {
        }

        return { values: data, errors: formattedErrors };
      }
    },
  });
  const classes = useStyles();

  const filter = new Filter({ placeHolder: '*' });
  filter.addWords(...profinityWords);
  useUpdateEffect(() => {
    if (cancel && notes?.trim()?.length === 0) {
      setValidateNotes(false);
      setCancel(false);
    } else if (notes?.trim()?.length === 0 && !save) {
      setValidateNotes(true);
    } else {
      setValidateNotes(false);
    }
  }, [notes?.length]);
  useEffect(() => {
    setNotes(specNotesData && specNotesData.notes?.trim().length > 0 ? specNotesData.notes : '');
    reset(initialValues);
  }, [specNotesData]);
  const [webSocket, setWebSocket] = React.useState();
  const [inputSampleRate, setInputSampleRate] = React.useState();

  const handleEventStreamMessage = (messageJson) => {
    const results = messageJson.Transcript.Results;
    if (results.length > 0) {
      if (results[0].Alternatives.length > 0) {
        const transcript = decodeURIComponent(escape(results[0].Alternatives[0].Transcript));

        // if this transcript segment is final, add it to the overall transcription
        if (!results[0].IsPartial) {
          text = transcript.toLowerCase().replace(/\./g, '').replace('?', '').replace('!', '');
          transcription += filter.clean(text) + '\n';
          setNotes(`${notes ? notes : ''} ${transcription}`);
          setCharLimitMsg(true);
        }
      }
    }
  };

  const stop = (socket) => {
    if (socket) {
      socket.close();
      transcription = '';
      setPlay(false);
      setWebSocket(undefined);
    }
  };

  const convertAudioToBinaryMessage = (audioChunk) => {
    const raw = MicrophoneStream.toRaw(audioChunk);

    // downsample and convert the raw audio bytes to PCM
    const downsampledBuffer = audioUtils.downsampleBuffer(raw, inputSampleRate, sampleRate);
    const pcmEncodedBuffer = audioUtils.pcmEncode(downsampledBuffer);

    // add the right JSON headers and structure to the message
    const audioEventMessage = getAudioEventMessage(Buffer.from(pcmEncodedBuffer));

    // convert the JSON object + headers into a binary event stream message
    const binary = eventStreamMarshaller.marshall(audioEventMessage);

    return binary;
  };

  const streamAudioToWebSocket = (userMediaStream) => {
    // let's get the mic input from the browser, via the microphone-stream module
    const micStream = new MicrophoneStream();

    micStream.on('format', function (data) {
      setPlay(true);
      setInputSampleRate(data.sampleRate);
    });

    micStream.setStream(userMediaStream);

    // Pre-signed URLs are a way to authenticate a request (or WebSocket connection, in this case)
    // via Query Parameters. Learn more: https://docs.aws.amazon.com/AmazonS3/latest/API/sigv4-query-string-auth.html
    const url = createPresignedUrl();

    // open up our WebSocket connection
    const socket = new WebSocket(url);
    socket.binaryType = 'arraybuffer';
    // when we get audio data from the mic, send it to the WebSocket if possible
    socket.onopen = function () {
      micStream.on('data', function (rawAudioChunk) {
        // the audio stream is raw audio bytes. Transcribe expects PCM with additional metadata, encoded as binary
        const binary = convertAudioToBinaryMessage(rawAudioChunk);

        if (socket.readyState === socket.OPEN) {
          socket.send(binary);
        }
      });
    };

    // handle inbound messages from Amazon Transcribe
    socket.onmessage = function (message) {
      // convert the binary event stream message to JSON
      const messageWrapper = eventStreamMarshaller.unmarshall(Buffer.from(message.data));
      const messageBody = JSON.parse(String.fromCharCode(...messageWrapper.body));
      if (messageWrapper.headers[':message-type'].value === 'event') {
        handleEventStreamMessage(messageBody);
      } else {
        console.error(messageBody.Message);
        stop(socket);
      }
    };

    socket.onerror = function () {
      stop(socket);
    };

    socket.onclose = function (closeEvent) {
      micStream.stop();

      // need to understand

      // the close event immediately follows the error event; only handle one.
    };

    setWebSocket(socket);
  };

  const handleStart = () => {
    // first we get the microphone input from the browser (as a promise)...
    window.navigator.mediaDevices
      .getUserMedia({
        video: false,
        audio: true,
      })
      // ...then we convert the mic stream to binary event stream messages when the promise resolves
      .then(streamAudioToWebSocket)
      .catch(function (error) {});
  };
  const handleRegistration = (data) => {
    if (data?.notes && accountNumber) {
      const filteredNote = data.notes ? filter.clean(data.notes) : '';
      const text = filteredNote ? filteredNote.replace(/\*/g, '') : '';

      dispatch(
        postSpecNotes({
          accountNumber: Number(accountNumber),
          notes: text,
        }),
      );
      setTimeout(() => {
        dispatch(successMessage());
      }, 6000);
    }
  };

  const handleSetNotes = (value) => {
    for (const i in profinityWords) {
      if (value === profinityWords[i] && value.length > 1) {
        const replacedWord =
          value.length === 2
            ? value.replace(value, '**')
            : value.length === 3
              ? value.replace(value, '***')
              : value.replace(value, '****');
        setNotes(replacedWord);
        break;
      }
    }
  };
  const debouncedSave = useCallback(
    debounce(
      (nextValue) =>
        nextValue.length <= 4 ? handleSetNotes(nextValue) : setNotes(filter.clean(nextValue)),
      2000,
    ),
    [],
  );
  useEffect(() => {
    if (!notes) {
      debouncedSave.cancel();
    }
  }, [notes]);
  const { roleData } = useSelector(({ role }) => role);
  const workQueuesAccess = roleData?.window?.['Work Queue']?.WQ;
  const { showDashboardWorkQueue } = useSelector(({ role }) => role);

  useEffect(() => {
    if (location.pathname === '/account-summary' && !accountNumber) {
      setNotes('');
      dispatch(setNotesReduxState({ property: 'specNotesData', value: {} }));
    }
  }, [accountNumber]);

  useEffect(() => {
    reset(initialValues);
  }, [modifiedWindowsList]);
  const validationSchema = Yup.object().shape({
    notes: Yup.string()
      .required('Important Notes is required')
      .max(500, 'Cannot exceed 500 characters'),
  });
  const handleReset = () => {
    dispatch(handleResetButton({ windowName: IMPORTANT_NOTES }));
  };
  const handleCancel = () => {
    setValidateNotes(false);
    setCancel(true);
    transcription = '';
    setNotes(specNotesData?.notes?.trim()?.length > 0 ? specNotesData.notes : '');
    debouncedSave.cancel();
    stop(webSocket);
  };
  const handleFilter = (value) => {
    setSave(false);
    setCharLimitMsg(true);
    const nextValue = value;
    setNotes(nextValue);
    nextValue && debouncedSave(nextValue);
  };

  const isRegisteredRef = useRef(false);
  const { updatedForms, addFormCode } = useContext(FormUpdatedContext);
  const { registerSaveFunction, unregisterSaveFunction } = useContext(SaveWindowDataContext);
  const { registerResetFunction, unregisterResetFunction } = useContext(ResetWindowDataContext);
  const { saveWindow } = useSaveWindow();
  const handleBlurEvent = () => {
    addFormCode('10');
  };

  useEffect(() => {
    if (updatedForms.includes('10') && !isRegisteredRef.current) {
      registerSaveFunction('10', handleSubmit(handleRegistration));
      registerResetFunction('10', () => {
        handleCancel();
        handleReset();
      });
      isRegisteredRef.current = true;

      return () => {
        unregisterSaveFunction('10');
        unregisterResetFunction('10');
        isRegisteredRef.current = false;
      };
    }
  }, [
    updatedForms,
    registerSaveFunction,
    unregisterSaveFunction,
    registerResetFunction,
    unregisterResetFunction,
  ]);

  return (
    <>
      <Accordion
        windowName="Important Notes"
        windowCode="10"
        className={`new-standard-textfields ${classes.heading}`}
      >
        <div className={classes.accordionDetailsStyling}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              saveWindow('10');
            }}
          >
            <div
              className={`internal-notepad new-standard-textfields mb-2 ${classes.specialNeeds} ${classes.notesError}`}
            >
              <TextField
                multiline={true}
                minRows={6}
                margin="dense"
                disabled={play}
                spellCheck="true"
                variant="outlined"
                className={`${classes.labelName}`}
                placeholder={`Press start and speak into your mic or Enter Notes`}
                defaultValue={notes}
                inputRef={register}
                error={validateNotes || notes?.length > 500}
                onBlur={handleBlurEvent}
                onChange={(e) => {
                  handleFilter(e?.target?.value?.trimStart());
                  markAsModified(
                    {
                      field: 'notes',
                      newVal: e.target.value,
                      oldVal: getSpecialNotesInitialVal(specNotesData, 'notes'),
                      label: 'Important Notes',
                    },
                    onDirty,
                  );
                  handleBlurEvent();
                }}
                label={'Important Notes'}
                inputProps={{
                  spellCheck: true,
                  lang: 'en',
                  maxLength: 500,
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                name="notes"
                fullWidth={true}
              />
              {!errors?.notes && notes?.length > 0 && notes?.length < 500 && charLimitMsg && (
                <FormHelperText
                  style={{
                    color: 'blue',
                  }}
                  variant="outlined"
                >
                  Allows {500 - notes?.length} characters only.
                </FormHelperText>
              )}
              {notes?.length > 0 && notes?.length > 500 && (
                <FormHelperText className="ml-3">Note cannot exceed 500 characters</FormHelperText>
              )}
              {validateNotes && (
                <FormHelperText className="ml-3">Important Notes is required</FormHelperText>
              )}
              <Tooltip title="Start Recording">
                <Button className={classes.micButton} disabled={play} onClick={handleStart}>
                  <MicIcon />
                </Button>
              </Tooltip>
              <Tooltip title="Stop Recording">
                <Button className={classes.stopButton} onClick={() => stop(webSocket)}>
                  <StopRoundedIcon />
                </Button>
              </Tooltip>
            </div>
            <div className={`d-flex justify-content-center mb-2 new-standard-textfields`}>
              <Button
                variant="outlined"
                className={`reset-btn mr-1 ${classes.buttonRoot}`}
                onClick={() => {
                  reset(initialValues);
                  handleCancel();
                  handleReset();
                }}
              >
                Cancel
              </Button>
              <Button
                variant="outlined"
                type="submit"
                onClick={(e) => {
                  e.preventDefault();
                  saveWindow('10');
                }}
                className={`search-btn ml-1 ${classes.buttonRoot}`}
                disabled={
                  isLoading ||
                  (specNotesData?.notes?.trim() === '' &&
                    !roleData?.window?.['Core Windows']?.W10?.add) ||
                  (specNotesData?.notes !== ' ' &&
                    !roleData?.window?.['Core Windows']?.W10?.edit) ||
                  (SPLIT_SCREEN.ARRAY_VALUES.includes(showDashboardWorkQueue) &&
                    !workQueuesAccess?.edit &&
                    !workQueuesAccess?.add) ||
                  !accountNumber
                }
              >
                Save
              </Button>
            </div>
          </form>
          {/* {accountNumber && <InternalNotePadTable setNotesCount={setNotesCount} />} */}
          {isLoading && <PageLoader />}
          {errorMsg && <ErrorSnackbar errorMsg={errorMsg} />}
          {successMsg && <SuccessSnackbar successMsg={successMsg} />}
        </div>
      </Accordion>
    </>
  );
};

export default SpecialNotes;

function createPresignedUrl() {
  const endpoint = 'transcribestreaming.' + region + '.amazonaws.com:8443';

  // get a preauthenticated URL that we can use to establish our WebSocket
  return v4.createPresignedURL(
    'GET',
    endpoint,
    '/stream-transcription-websocket',
    'transcribe',
    crypto.createHash('sha256').update('', 'utf8').digest('hex'),
    {
      key: REACT_APP_ACCESS_KEY,
      secret: REACT_APP_SECRET_KEY,
      protocol: 'wss',
      expires: 15,
      region: region,
      query: 'language-code=' + languageCode + '&media-encoding=pcm&sample-rate=' + sampleRate,
    },
  );
}
